import { DDSTypography } from 'den-design-system'
import React from 'react'
import SFDistributionBar from './common/SFDistributionBar'
import Tooltip from '../New/Tooltip/Tooltip'
import UsageBar from './UsageBar'
import { generateColors } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'

interface LicenseDistributionCardProps {
  usage: number
  mostCommonLicense: string
  totalLicenses: number
  usedLicenses: number
  distributions: any[]
  link?: string
  colorMapping?: Map<string, string>
}

const SFLicenseDistributionCard: React.FC<LicenseDistributionCardProps> = ({
  usage,
  mostCommonLicense,
  totalLicenses,
  usedLicenses,
  distributions,
  colorMapping
}) => {
  const fallbackColors = generateColors(distributions?.length)

  return (
    <div
      style={{
        borderColor: pallete.colors.stroke2,
        borderRadius: '8px'
      }}
      className='w-full max-w-[100%] min-h-[200px] border p-[16px] space-y-[12px] shadow-sm bg-white flex flex-col'
    >
      <div className='flex flex-wrap justify-between gap-4'>
        {/* Usage */}
        <div className='w-[19%] min-w-[160px] flex flex-col gap-2'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
          >
            Usage
          </DDSTypography.Title>
          <DDSTypography.Title
            type='h4'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            {isNaN(usage) || !isFinite(usage)
              ? '-'
              : usage > 0 && usage < 0.01
                ? `${usage.toFixed(5)}%`
                : `${usage.toFixed(2)}%`}
          </DDSTypography.Title>
          <UsageBar usage={usage} />
        </div>

        <div className='min-w-[160px] max-w-[220px] flex flex-col gap-1'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
          >
            Most Common License
          </DDSTypography.Title>

          <Tooltip
            id={`${mostCommonLicense}`}
            position='top'
            className={`max-w-[80%] ${
              typeof mostCommonLicense === 'string' &&
              mostCommonLicense.length > 20
                ? mostCommonLicense
                : '!hidden'
            }`}
            label={
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark4}
                className='max-w-[900px] break-words'
              >
                {mostCommonLicense}
              </DDSTypography.Paragraph>
            }
          >
            <div className='truncate max-w-[200px]'>
              <DDSTypography.Title
                type='h4'
                variant='semiBold'
                color={pallete.colors.textDark3}
                className='max-w-[900px] truncate break-words'
              >
                {mostCommonLicense}
              </DDSTypography.Title>
            </div>
          </Tooltip>
        </div>

        <div className='min-w-[120px] flex flex-col gap-1'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
          >
            Total License
          </DDSTypography.Title>
          <DDSTypography.Title
            type='h4'
            variant='semiBold'
            color={pallete.colors.textDark3}
          >
            {totalLicenses !== -1
              ? totalLicenses?.toLocaleString('en-US')
              : '-'}
          </DDSTypography.Title>
        </div>

        <div className='min-w-[120px] flex flex-col gap-1'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
          >
            Used Licenses
          </DDSTypography.Title>
          <DDSTypography.Title
            type='h4'
            variant='semiBold'
            color={pallete.colors.textDark3}
          >
            {usedLicenses !== -1 ? usedLicenses?.toLocaleString('en-US') : '-'}
          </DDSTypography.Title>
        </div>

        <div className='min-w-[120px] flex flex-col gap-1'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
          >
            Available
          </DDSTypography.Title>
          <DDSTypography.Title
            type='h4'
            variant='semiBold'
            color={pallete.colors.textDark3}
          >
            {totalLicenses !== -1
              ? (totalLicenses - usedLicenses)?.toLocaleString('en-US')
              : '-'}
          </DDSTypography.Title>
        </div>

        <div className='w-full mt-4'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark4}
            className='mb-1'
          >
            Used License Type Distribution
          </DDSTypography.Title>
          <SFDistributionBar
            distributions={distributions.map((d, i) => {
              return {
                name: d.name,
                value: isNaN(d.usage) ? 0 : d.usage,
                color: colorMapping
                  ? colorMapping.get(d.name) || '#cccccc'
                  : fallbackColors[i]
              }
            })}
          />
        </div>
      </div>
    </div>
  )
}

export default SFLicenseDistributionCard
